import React, {useState, useEffect} from 'react';
import { Link } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { getLeaderboard } from '../../api/Api';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import Typography from '@mui/material/Typography';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import { useColors, useColorMode } from '../../theme/ColorModeContext';

const ranks = [
  { value: 0, label: "Novice" },
  { value: 20, label: "Explorer" },
  { value: 40, label: "Master" },
  { value: 60, label: "Hacker" },
  { value: 80, label: "Sentinel" },
  { value: 100, label: "Overlord" },
]

const rankToText = (rank: number) => {
    if(rank < 20) return ranks[0].label;
    else if(rank < 40) return ranks[1].label;
    else if(rank < 60) return ranks[2].label;
    else if(rank < 80) return ranks[3].label;
    else if(rank < 100) return ranks[4].label;
    else return ranks[5].label;
};

// Darker shades used in light mode for contrast on light backgrounds
const topThreeColorsDark  = ['#ffd740', '#b0bec5', '#ff8a65'];
const topThreeColorsLight = ['#c47d00', '#546e7a', '#bf5630'];

const MedalIcon = ({ place }: { place: number }) => {
    const medalColors: Record<number, { fill: string; stroke: string }> = {
        0: { fill: '#ffd740', stroke: '#f9a825' },
        1: { fill: '#b0bec5', stroke: '#78909c' },
        2: { fill: '#ff8a65', stroke: '#e64a19' },
    };
    const c = medalColors[place];
    return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="14" r="7" fill={c.fill} stroke={c.stroke} strokeWidth="1.5" />
            <path d="M8 2L10 9H14L16 2" stroke={c.stroke} strokeWidth="1.5" fill="none" />
            <text x="12" y="17" textAnchor="middle" fontSize="8" fontWeight="bold" fill={c.stroke}>
                {place + 1}
            </text>
        </svg>
    );
};

interface PropsLeaderboard {
    token: string;
    username?: string;
}

interface User {
    id: string;
    username: string;
    points: number;
}

const PAGINATION = 50;

const Leaderboard = (props: PropsLeaderboard) => {
    const colors = useColors();
    const { mode } = useColorMode();
    const topThreeColors = mode === 'dark' ? topThreeColorsDark : topThreeColorsLight;
    const [users, setUsers] = useState<User[]>([]);
    const [total, setTotal] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [page, setPage] = useState<Number>(0);
    const token = props.token;

    const rankColors: Record<string, string> = {
        novice: colors.rank.novice,
        explorer: colors.rank.explorer,
        master: colors.rank.master,
        hacker: colors.rank.hacker,
        sentinel: colors.rank.sentinel,
        overlord: colors.rank.overlord,
    };

    useEffect(() => {
        const fetchData = async () => {
            const data = await getLeaderboard(token);
            setUsers(data.users);
            setTotal(data.total);
            setLoaded(true);
        }
        fetchData();
    }, []);

    const topThree = users.slice(0, 3);
    // Reorder for podium: [2nd, 1st, 3rd]
    const podiumOrder = topThree.length >= 3
        ? [topThree[1], topThree[0], topThree[2]]
        : topThree;
    const podiumPlaces = topThree.length >= 3 ? [1, 0, 2] : topThree.map((_, i) => i);

    return (
        <PageContainer>
            <CyberpunkTitle title="Leaderboard" mb="24px" />

            {/* Top 3 Podium */}
            {topThree.length >= 3 && (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-end',
                    gap: { xs: '8px', md: '16px' },
                    marginBottom: '32px',
                    padding: '0 16px',
                }}>
                    {podiumOrder.map((user, idx) => {
                        const place = podiumPlaces[idx];
                        const isFirst = place === 0;
                        const rankText = total ? rankToText(user.points * 100 / total) : 'Novice';
                        const rankColor = rankColors[rankText.toLowerCase()] || colors.text.secondary;

                        return (
                            <Link key={user.id} to={`/profile/${user.username}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <GlowCard
                                    glowOnHover
                                    gradientBorder={isFirst}
                                    sx={{
                                        padding: isFirst ? '28px 24px' : '20px 20px',
                                        textAlign: 'center',
                                        width: { xs: '110px', md: isFirst ? '200px' : '170px' },
                                        transform: isFirst ? 'none' : 'translateY(16px)',
                                        ...(isFirst && {
                                            boxShadow: `0 0 30px ${topThreeColors[0]}22, 0 0 60px ${topThreeColors[0]}11`,
                                        }),
                                    }}
                                >
                                    <Box sx={{ marginBottom: '12px' }}>
                                        <MedalIcon place={place} />
                                    </Box>
                                    <Box sx={{
                                        display: 'inline-block',
                                        borderRadius: '50%',
                                        padding: '3px',
                                        border: `2px solid ${rankColor}`,
                                        boxShadow: `0 0 12px ${rankColor}30`,
                                        marginBottom: '12px',
                                    }}>
                                        <img
                                            src={`/images/ranks/${rankText.toLowerCase()}.png`}
                                            alt={rankText}
                                            style={{
                                                width: isFirst ? '72px' : '56px',
                                                height: isFirst ? '72px' : '56px',
                                                borderRadius: '50%',
                                                display: 'block',
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{
                                        fontFamily: '"Orbitron", sans-serif',
                                        fontWeight: 600,
                                        color: topThreeColors[place],
                                        marginBottom: '4px',
                                        fontSize: isFirst ? '0.95rem' : '0.85rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {user.username}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        color: colors.text.secondary,
                                        fontFamily: '"Orbitron", sans-serif',
                                    }}>
                                        {user.points} pts
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        display: 'block',
                                        color: rankColor,
                                        marginTop: '2px',
                                        fontSize: '0.7rem',
                                    }}>
                                        {rankText}
                                    </Typography>
                                </GlowCard>
                            </Link>
                        );
                    })}
                </Box>
            )}

            <GlowCard glowOnHover={false}>
                <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" sx={{ width: '50px' }}>#</TableCell>
                      <TableCell align="left" sx={{ width: '50px' }}></TableCell>
                      <TableCell align="left">Username</TableCell>
                      <TableCell align="left">Rank</TableCell>
                      <TableCell align="right">Points</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                  {users.filter((user, idx) => idx >= Number(page) * PAGINATION && idx < Number(page) * PAGINATION + PAGINATION).map((user, idx) => {
                      const globalIdx = Number(page) * PAGINATION + idx;
                      const rankText = total ? rankToText(user.points * 100 / total) : 'Novice';
                      const rankColor = rankColors[rankText.toLowerCase()] || colors.text.secondary;
                      const isTopThree = globalIdx < 3 && Number(page) === 0;
                      const isCurrentUser = props.username && user.username === props.username;

                      return (
                          <TableRow key={idx} sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                              ...(isTopThree && {
                                  backgroundColor: `${topThreeColors[globalIdx]}08`,
                              }),
                              ...(isCurrentUser && {
                                  backgroundColor: 'rgba(185, 28, 28, 0.08)',
                                  borderLeft: `2px solid ${colors.accent.cyan}`,
                              }),
                          }}>
                            <TableCell align="right" sx={{
                                fontWeight: isTopThree ? 700 : 400,
                                color: isTopThree ? topThreeColors[globalIdx] : colors.text.muted,
                                fontFamily: '"Orbitron", sans-serif',
                                fontSize: isTopThree ? '1.1rem' : '0.875rem',
                            }}>
                                {isTopThree ? <MedalIcon place={globalIdx} /> : globalIdx + 1}
                            </TableCell>
                            <TableCell align="left">
                                <img style={{
                                    width: '32px',
                                    height: '32px',
                                    overflow: 'hidden',
                                    borderRadius: '50%',
                                    border: `2px solid ${rankColor}`,
                                    boxShadow: `0 0 8px ${rankColor}30`,
                                }} src={`/images/ranks/${rankText.toLowerCase()}.png`} alt={rankText} />
                            </TableCell>
                            <TableCell align="left" sx={{ fontWeight: 500 }}>
                              <Link style={{ textDecoration: 'none' }} to={`/profile/${user.username}`}>
                                <Box sx={{
                                    color: isTopThree ? topThreeColors[globalIdx] : colors.text.primary,
                                    '&:hover': { color: colors.accent.cyan },
                                    transition: 'color 0.2s ease',
                                    fontWeight: isTopThree ? 600 : 400,
                                }}>
                                    {user.username}
                                </Box>
                              </Link>
                            </TableCell>
                            <TableCell align="left" sx={{ color: rankColor }}>
                                {rankText}
                            </TableCell>
                            <TableCell align="right" sx={{
                                fontWeight: 500,
                                fontFamily: '"Orbitron", sans-serif',
                            }}>
                                {user.points}
                            </TableCell>
                          </TableRow>
                      );
                  })}
                  </TableBody>
                </Table>
                </TableContainer>
            </GlowCard>

            <Box sx={{ textAlign: 'center', marginTop: '24px', display: 'flex', justifyContent: 'center', gap: '4px' }}>
            {[...Array(Math.floor((users.length + PAGINATION - 1) / PAGINATION))].map((x, i) =>
                <Button
                  key={i}
                  onClick={() => setPage(i)}
                  sx={{
                      minWidth: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      fontFamily: '"Orbitron", sans-serif',
                      fontWeight: 600,
                      ...(page === i ? {
                          backgroundColor: colors.bg.surface,
                          color: colors.text.primary,
                          border: `1px solid ${colors.border.strong}`,
                      } : {
                          color: colors.text.secondary,
                          backgroundColor: colors.bg.tertiary,
                          '&:hover': {
                              backgroundColor: colors.bg.surface,
                          },
                      }),
                  }}
                >
                  {i + 1}
                </Button>
              )}
            </Box>
            {!loaded && <Box sx={{ textAlign: 'center', marginTop: '40px' }}><CircularProgress size={60} /></Box>}
            {loaded && !users.length && (
                <Box sx={{ textAlign: 'center', marginTop: '40px' }}>
                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                        No ranked users yet. Be the first to score points.
                    </Typography>
                </Box>
            )}
        </PageContainer>
    )
};

export default Leaderboard;
